import {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod/mini';
import useTranslation from '~/hooks/useTranslation';
import bikerHeightStyles from '~/styles/bookingFlow/biker-height.module.css';
import TextField from '~/components/Form/TextField';
import type { PackageInformationRequest } from '~/types/api';
import { OrderLineSchema } from '~/validation/api/packageSessionResponse';
import InformationCard from './InformationCard';

type Props = {
  registerFieldPath: string;
  className?: string;
  orderLines: z.infer<typeof OrderLineSchema>[];
};

export default function BikeRiderHeight({
  registerFieldPath,
  className,
  orderLines,
}: Props) {
  const translate = useTranslation();
  const { setValue } = useFormContext<PackageInformationRequest>();

  const participants = useWatch({ name: 'participants' }) as PackageInformationRequest['participants'];
  const pax = useMemo(() => participants.length, [participants]);

  let value = useWatch({ name: registerFieldPath }) as PackageInformationRequest['bike_rider_height'];

  if (!value) {
    value = [];
  }

  /**
   * Returns true if any bike order lines are selected.
   *
   * We check if any order line has a quantity greater than 0 and if the unit
   * type is either 'bikedays' or 'bikenights'.
   */
  const hasBikeOrderLinesSelected = useMemo(() => {
    return orderLines?.some((orderLine) => {
      if (!orderLine.quantities.some((quantity) => quantity > 0)) {
        return false;
      }

      return orderLine.unit_type === 'bikedays' || orderLine.unit_type === 'bikenights';
    });
  }, [orderLines]);

  /**
   * Returns the bike order line for a given participant index.
   *
   * Order lines have quantities, for example [1, 0, 0], meaning the first
   * participant has that order line selected and participant 2 and 3 do not.
   *
   * We search through all bike order lines and return the first one that has
   * a quantity for the given participant index.
   */
  const bikeForParticipant = useCallback((participantIndex: number) => {
    return orderLines?.find((orderLine) => {
      if (orderLine.unit_type !== 'bikedays' && orderLine.unit_type !== 'bikenights') {
        return false;
      }

      const quantityForParticipant = orderLine.quantities[participantIndex];

      return quantityForParticipant > 0;
    });
  }, [orderLines]);

  /**
   * Ensures that the bike rider height values are always set for all participants.
   * So if the component is renedered for 4 pax, and the value does not have 4 items,
   * we add 4 items to the value.
   */
  useEffect(() => {
    if (!hasBikeOrderLinesSelected) {
      return;
    }

    if (pax && pax !== value.length) {
      const newBikeRiderHeight = value;

      for (let i = 0; i < pax; i += 1) {
        if (!newBikeRiderHeight[i]) {
          newBikeRiderHeight.push({
            participant_index: i,
            height_cm: 0,
          });
        }
      }

      newBikeRiderHeight.splice(pax);

      setValue(registerFieldPath as any, newBikeRiderHeight);
    }
  }, [hasBikeOrderLinesSelected, pax, registerFieldPath, setValue, value]);

  /**
   * If no bike order lines are selected, we clear the bike rider height values.
   * This is to prevent the data from containing old data.
   */
  useEffect(() => {
    if (hasBikeOrderLinesSelected) {
      return;
    }

    if (!value || value.length === 0) {
      return;
    }

    setValue(registerFieldPath as 'bike_rider_height', null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [hasBikeOrderLinesSelected, registerFieldPath, setValue, value]);

  if (!hasBikeOrderLinesSelected) {
    return null;
  }

  return (
    <InformationCard
      title={translate('bookingFlow.information.bike_rider_height')}
      description={translate('bookingFlow.information.bike_rider_height_description')}
      className={className}
      paddingLess
    >
      <div className={bikerHeightStyles.rows}>
        {value.map((row, index) => {
          const currentParticipant = participants
            .find((participant) => participant.index === row.participant_index);

          const bikeOrderLine = bikeForParticipant(index);

          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${row.participant_index}_${index}`}
              className={bikerHeightStyles.row}
            >
              <div className={bikerHeightStyles.columnName}>
                <strong>
                  {
                    currentParticipant?.first_name && currentParticipant?.last_name
                      ? `${currentParticipant.first_name} ${currentParticipant.last_name}`
                      : `${translate('bookingFlow.information.participant')} ${index + 1}`
                  }
                </strong>

                {bikeOrderLine?.name && (
                  <div className={bikerHeightStyles.bikeOrderLineName}>
                    {bikeOrderLine.name}
                  </div>
                )}
              </div>

              <div className={bikerHeightStyles.columnHeight}>
                <TextField
                  label={translate('bookingFlow.information.height_cm')}
                  registerField={`${registerFieldPath}.${index}.height_cm`}
                  fieldType="number"
                  min={0}
                  max={260}
                  affix="cm"
                  visuallyHiddenLabel
                  selectOnFocus
                />
              </div>
            </div>
          );
        })}
      </div>
    </InformationCard>
  );
}
